/**
 * My Reviews Page - User can view and manage their reviews
 * Includes reviews for products, shops, drivers, and delivery service
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Link2,
  ArrowLeft,
  Star,
  StarHalf,
  Package,
  Store,
  Truck,
  MessageSquare,
  Trash2,
  CheckCircle,
  Image,
  Loader2,
  Filter
} from 'lucide-react';

const REVIEW_TYPES = [
  { id: 'all', label: 'All Reviews', icon: Star },
  { id: 'product', label: 'Products', icon: Package },
  { id: 'shop', label: 'Shops', icon: Store },
  { id: 'driver', label: 'Drivers', icon: Truck },
  { id: 'delivery', label: 'Deliveries', icon: Package }
];

const StarRating = ({ rating, size = 'md' }) => {
  const sizeClass = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-6 w-6' : 'h-4 w-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
        />
      ))}
    </div>
  );
};

const MyReviews = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteLoading, setDeleteLoading] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, [filter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?target_type=${filter}` : '';
      const response = await api.get(`/enhanced-reviews/mine${params}`);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    setDeleteLoading(reviewId);
    try {
      await api.delete(`/enhanced-reviews/${reviewId}`);
      toast.success('Review deleted');
      setReviews(reviews.filter(r => r.id !== reviewId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete review');
    } finally {
      setDeleteLoading(null);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'product': return Package;
      case 'shop': return Store;
      case 'driver': return Truck;
      case 'delivery': return Package;
      default: return Star;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded border border-border">
                MY REVIEWS
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {REVIEW_TYPES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                filter === id
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
              data-testid={`filter-${id}`}
            >
              <Icon className="h-4 w-4" />
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20">
            <Star className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Reviews Yet</h3>
            <p className="text-muted-foreground mb-6">
              {filter === 'all' 
                ? "You haven't written any reviews yet."
                : `You haven't reviewed any ${filter}s yet.`}
            </p>
            <Button
              onClick={() => navigate('/shop')}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Browse Shop
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => {
              const TypeIcon = getTypeIcon(review.target_type);
              return (
                <div
                  key={review.id}
                  className="bg-card border border-border rounded-lg p-5"
                  data-testid={`review-${review.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <TypeIcon className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {review.target_name || `${review.target_type} #${review.target_id?.slice(0, 8)}`}
                          </p>
                          <p className="text-xs text-muted-foreground capitalize">{review.target_type}</p>
                        </div>
                        {review.is_verified_purchase && (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 border border-green-500/30">
                            <CheckCircle className="h-3 w-3" />
                            Verified
                          </span>
                        )}
                      </div>

                      {/* Rating */}
                      <div className="flex items-center gap-3 mb-3">
                        <StarRating rating={review.rating} />
                        <span className="text-sm text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Comment */}
                      <p className="text-foreground text-sm leading-relaxed mb-3">
                        {review.comment}
                      </p>

                      {/* Photos */}
                      {review.photos && review.photos.length > 0 && (
                        <div className="flex gap-2 mb-3">
                          {review.photos.map((photo, idx) => (
                            <img
                              key={idx}
                              src={photo}
                              alt={`Review photo ${idx + 1}`}
                              className="w-16 h-16 rounded-lg object-cover border border-border"
                            />
                          ))}
                        </div>
                      )}

                      {/* Response */}
                      {review.response && (
                        <div className="mt-4 p-3 bg-muted/50 rounded-lg border-l-2 border-primary">
                          <p className="text-xs text-muted-foreground mb-1">
                            Response from {review.response.responder_name}
                          </p>
                          <p className="text-sm text-foreground">{review.response.text}</p>
                        </div>
                      )}

                      {/* Stats */}
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span>{review.helpful_count || 0} found helpful</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteReview(review.id)}
                      disabled={deleteLoading === review.id}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`delete-${review.id}`}
                    >
                      {deleteLoading === review.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyReviews;
